import { useCallback, useEffect, useState } from 'react'
import { IconPhoto, IconFiles, IconCloudUpload, IconX } from '@tabler/icons'
import { ReactComponent as DropFile } from './assets/drop.svg'
import { useDropzone } from 'react-dropzone';
import axios from 'axios'
import Gallery from './components/Gallery';
import Folders, { MediaFolderTypes } from './components/Folders';
import { useQuery } from '@apollo/client';
import { MEDIA_FOLDERS } from '../../graphql/query/mediaFolders';
import Folder from './components/Folder';
import { getS3Url, appendS3UploadAuthFields } from '../../utils';

interface MediaLibraryProps {
  siteId: number,
  handleClose: () => void,
  done: () => void,
  mediaType?: string,
  uid?: string,
  campaignId?: string | number | Array<string | number>,
  layout?: 'modal' | 'page',
  standalone?: boolean,
  hideCloseButton?: boolean,
}

let folderId: any;

const MediaLibrary = (props: MediaLibraryProps) => {
  const { siteId, handleClose, done, mediaType, uid, campaignId, layout = 'modal', standalone = false, hideCloseButton = false } = props
  const [mediaKind, setMediaKind] = useState<'image' | 'document'>(() => (mediaType === 'document' ? 'document' : 'image'))
  const [error, setError] = useState(false)
  const [status, setStatus] = useState({ uploaded: false, loading: true })
  const [fileUrl, setUrl] = useState<any>(null)
  const [progress, setProgress] = useState<any>(null)
  const [currentFolderId, setCurrentFolderId] = useState<any>(null)
  const [jobId, setJobId] = useState<any>(null)
  const [count, setCount] = useState<any>({
    images: 0,
    documents: 0,
  })

  folderId = currentFolderId;

  useEffect(() => {
    if(Array.isArray(campaignId)) {
      campaignId.forEach((id: any) => {
        const selector = `#html-editor-content-${id}`
        document.querySelector(selector)?.classList.add('pointer-events-none')
      })
    } else {
      const selector = `#html-editor-content-${campaignId}`
      document.querySelector(selector)?.classList.add('pointer-events-none')
    }

    return () => {
      if(Array.isArray(campaignId)) {
        campaignId.forEach((id: any) => {
          const selector = `#html-editor-content-${id}`
          document.querySelector(selector)?.classList.remove('pointer-events-none')
        })
      } else {
        const selector = `#html-editor-content-${campaignId}`
        document.querySelector(selector)?.classList.remove('pointer-events-none')
      }
    }

  }, [])

  const refreshAttachmentCounts = useCallback(async () => {
    try {
      const counts = await axios.get(`//${window.appDomain}/attachments/attachments_count?site_id=${siteId}&token=${window.token}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      })
      if (counts.status === 200) {
        setCount(counts.data)
      } else {
        setCount({ images: 0, documents: 0 })
      }
    } catch {
      setCount({ images: 0, documents: 0 })
    }
  }, [siteId])

  useEffect(() => {
    refreshAttachmentCounts()
  }, [status?.uploaded, refreshAttachmentCounts])

  useEffect(() => {
    const getUploadStatus = async (id:string) => {
      const response = await axios.get(`//${window.appDomain}/attachments/status.json?job_id=${id}&site_id=${siteId}&token=${window.token}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      })
      return response
    }

    if (!jobId || !Object.keys(jobId)?.length) return

    const interval = setInterval(async () => {
      Object.values(jobId).forEach(async (val: any) => {
        const response = await getUploadStatus(val)
        if (response.status == 200) {
          const { data } = response
          if(data.status == "complete" || data.status == "failed" || data.status == "interrupted") {
            const key = data.status == "complete" ? data.attachment.asset_file_name : Object.keys(jobId).find(key => jobId[key] === val)
            clearInterval(interval)
            setJobId((prev: any) => {
              delete prev[key]
              return prev
            })
            setProgress((prev: any) => {
              delete prev[key]
              return prev
            })
            if (
              standalone &&
              data.status == "complete" &&
              data.attachment?.asset_content_type === "application/pdf"
            ) {
              setMediaKind("document")
            }
            setUrl([key])
            setStatus((prev: any) => ({ ...prev, uploaded: !status.uploaded }))
          }
        }
      })
    }, 3000)

    return () => {
      clearInterval(interval)
    }
  }, [jobId, status?.uploaded])

  const { data: { mediaFolders = [] } = {}, refetch } = useQuery(MEDIA_FOLDERS, {
    variables: {
      siteId: siteId
    }
  })

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setProgress((prev: any) => prev ? prev : {})
    setJobId((prev: any) => prev ? prev : {})
    setError(false)

    if (rejectedFiles.length > 0) {
      if (rejectedFiles[0].type === "application/pdf" || rejectedFiles[0].type === "image/jpeg") {
        setError(true)
      } else {
        setError(true)
      }
      return
    }

    if (acceptedFiles.length > 0) {
      let files = Array.from(acceptedFiles)

      files.forEach(async (acceptedFile: any) => {
        const file = acceptedFile
        const fileName = file.name.replace(/[^\w\-]/g, '_')
        const fileKey = `temp_media_assets/${new Date().getTime()}/${fileName}`
        let newFileName:any = fileName

        // File Preview
        const reader = new FileReader()
        reader.onload = (e: any) => {
          setUrl({ url: e.target?.result, fileName: newFileName })
        }
        reader.readAsDataURL(file)

        if (file.type === 'image/gif' && file.size > 2000000) {
          console.error('GIF file size exceeds 2MB, skipping upload')
          return setError(true)
        }

        const conditions = [
          { acl: "public-read" },
          { bucket: window?.domains?.aws_bucket },
          { "Content-Type": "image/" },
          { success_action_status: "200" },
          { key: fileKey },
          { "x-amz-meta-qqfilename": fileName },
          ["content-length-range", "0", "50000000"],
        ]

        // GET SIGNATURE
        const method = file.type === 'application/pdf' ? `signature_pdf` : `signature`
        const signatureResponse = await axios.post(`${window.token ? `//${window.appDomain}` : ''}/uploads/${method}`, { conditions })
        const { data, status: responseStatus } = signatureResponse
        if (responseStatus != 200) {
          setProgress((prev: any) => prev ? prev : {})
          setJobId((prev: any) => prev ? prev : {})
          setError(true)
          return
        }

        // UPLOAD TO S3
        let url = getS3Url()
        const { policy, signature, access_key_id, session_token, secret_access_key } = data
        const formData = new FormData()
        formData.set('key', fileKey)
        formData.set('Content-Type', file.type || 'image/png')
        formData.set('success_action_status', '200')
        if (!session_token) {
          formData.set('acl', 'public-read');
        }
        formData.set('x-amz-meta-qqfilename', fileName)
        formData.set('policy', policy)
        appendS3UploadAuthFields(formData, { policy, signature, access_key_id })
        if (session_token) {
          formData.set('x-amz-security-token', session_token)
        }
        formData.set("file", file, fileName)

        try {
          const s3response = await axios.post(url, formData, {
            onUploadProgress(progressEvent) {
              // set progress based on file key
              setProgress((prev: any) => {
                return {
                  ...prev,
                  [newFileName]: ((progressEvent?.loaded / progressEvent?.total) * 100).toFixed(0)
                }
              })
            },
          })
          if (s3response.status != 200) {
            setProgress((prev: any) => prev ? prev : {})
            setJobId((prev: any) => prev ? prev : {})
            setError(true)
            return
          }
        } catch (error) {
          setProgress((prev: any) => prev ? prev : {})
          setJobId((prev: any) => prev ? prev : {})
          setError(true)
          return
        }

        // SEND S3 URL TO SERVER
        const postObj = {
          s3_url: `${url}/${fileKey}`,
          aws_cred: {
            access_key_id, 
            session_token, 
            secret_access_key
          },
          ...(siteId && { site_id: siteId })
        }
        const attachmentResponse = await axios.post(`//${window.appDomain}/attachments.json?${folderId ? `&folder_id=${folderId}` : ''}&token=${window.token}`, postObj)
        if (attachmentResponse && attachmentResponse.status == 200 && attachmentResponse.data.job_id) {
          setJobId((prev: any) => {
            return {
              ...prev,
              [newFileName]: attachmentResponse.data.job_id
            }
          })
        } else {
          setError(true)
        }
      })
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: 'image/*, application/pdf',
    multiple: true,
    maxSize: 10485760,
    onDrop,
    noClick: true,
  })

  const effectiveMediaType = standalone ? mediaKind : (mediaType === 'document' ? 'document' : 'image')

  const galleryProps = {
    file: fileUrl,
    siteId,
    progress,
    status,
    setStatus,
    mediaFolders,
    currentFolderId,
    onDrop,
    done,
    handleClose,
    error,
    mediaType: effectiveMediaType,
    uid,
    standalone,
    onAttachmentsChanged: refreshAttachmentCounts,
  }

  const heightClass = layout === 'page' ? 'min-h-[calc(100vh-140px)] h-[calc(100vh-140px)]' : 'h-[528px]'

  return (
    <div className={`w-full ${heightClass} relative flex min-h-0`} {...getRootProps()}>
      <input {...getInputProps()} />
      <div className='w-[25%] shadow h-full min-h-0 flex flex-col'>
        <div className={standalone ? 'shrink-0 border-b' : 'h-[30%] border-b'}>
          <div className='p-6'>
            <div className='mb-4 text-secondary-light text-sm'>FILTERS</div>
            {
              Object.entries(count).map(([key, value]: any) => {
                const isActive = key === `${effectiveMediaType}s`
                const rowClass = standalone
                  ? `flex items-center py-[6px] px-4 -mx-4 w-[calc(100%+2rem)] rounded cursor-pointer mb-2 ${isActive ? 'bg-primary-lighter text-primary' : 'hover:bg-primary-lighter hover:text-primary'}`
                  : `flex items-center mb-4 cursor-pointer ${!isActive ? 'opacity-50 pointer-events-none' : ''}`
                const nextKind = key === 'images' ? 'image' : 'document'
                const countClass = standalone
                  ? isActive
                    ? 'text-xs bg-primary text-white rounded-full px-3 min-w-[40px] text-center'
                    : 'text-xs bg-primary-lighter text-primary rounded-full px-3 min-w-[40px] text-center'
                  : 'text-xs bg-primary-lighter text-primary rounded-full px-3 min-w-[40px] text-center'
                const rowContent = (
                  <>
                    {key === 'images' ? <IconPhoto stroke={1.25} /> : <IconFiles stroke={1.25} />}
                    <span className={`ml-2 mr-auto font-light capitalize`}>{key}</span>
                    <span className={countClass}>{value}</span>
                  </>
                )
                if (standalone) {
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`${rowClass} border-0 text-left font-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1`}
                      onClick={() => setMediaKind(nextKind)}
                      aria-pressed={isActive}
                    >
                      {rowContent}
                    </button>
                  )
                }
                return (
                  <div key={key} className={rowClass}>
                    {rowContent}
                  </div>
                )
              })
            }
          </div>
        </div>
        <div className={standalone ? 'flex min-h-0 flex-1 flex-col' : 'relative flex h-[70%] min-h-0 flex-col'}>
          <Folders
            siteId={Number(siteId)}
            currentFolderId={currentFolderId}
            setCurrentFolderId={setCurrentFolderId}
            standalone={standalone}
          >
            <>
              {
                mediaFolders?.map((folder: MediaFolderTypes) => {
                  const props = {
                    id: folder.id,
                    name: folder.name,
                    siteId: Number(siteId),
                    setCurrentFolderId,
                    currentFolderId,
                    refetch
                  }
                  return (
                    <Folder key={folder.id} {...props} />
                  )
                })
              }
            </>
          </Folders>
          {standalone ? (
            <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-3 text-center">
              <div className="text-xs text-gray-600">Drag & drop anywhere on this page to upload your files</div>
            </div>
          ) : (
            <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
              <div className='text-xs text-gray-600'>Drag & drop anywhere on this page to upload your files</div>
            </div>
          )}
        </div>
      </div>
      <div className='w-[75%] h-full p-6 bg-[#FAFAFD] rounded-r-lg'>
        <div className='flex justify-between items-center h-[8%]'>
          <div className='flex items-center'>
            <span className='text-xl text-secondary'>Media Library</span>
            <label htmlFor='fileUpload' className='flex text-primary font-light cursor-pointer'><IconCloudUpload className='mr-1 ml-3' stroke={1.25} /> Upload</label>
            <input id='fileUpload' type="file" className='hidden' multiple onChange={(e) => onDrop(e.target.files, [])} accept="image/*, application/pdf" />
          </div>
          {!hideCloseButton && (
            <div onClick={() => handleClose()} className='cursor-pointer' >
              <IconX size={18} />
            </div>
          )}
        </div>
        <Gallery {...galleryProps} />
      </div>
      {
        isDragActive &&
        <div className='absolute w-full h-full flex justify-center items-center' style={{ background: 'rgba(46, 56, 77, 0.4)' }}>
          <DropFile />
        </div>
      }
    </div>
  )
}

export default MediaLibrary